import { SearchFilterField, SelectFilterField } from '@/shared/ui';
import { useDebouncedValue } from '@/shared/utils';
import { Checkbox } from '@noorden-insurtech/alba-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useAdvisoryFilterOptions, useBulkBrokerCandidates } from '../hooks';
import type { BulkCandidateFilters } from '../hooks/use-bulk-broker-candidates.hook';
import {
  BULK_MAX_SELECTION,
  type BulkBrokerRef,
} from '../utils/bulk-broker-products.util';
import { BRAZIL_UF_SELECT_OPTIONS } from '@/features/proposals/components/capital-global/constants/brazil-uf-select-options.constant';

interface BulkProductsBrokerPickerProps {
  /** Filtros da tela, para o admin não recomeçar do zero. */
  initialFilters: BulkCandidateFilters;
  selected: Map<string, BulkBrokerRef>;
  onChange: (_next: Map<string, BulkBrokerRef>) => void;
  disabled?: boolean;
}

export function BulkProductsBrokerPicker({
  initialFilters,
  selected,
  onChange,
  disabled = false,
}: BulkProductsBrokerPickerProps) {
  // Estado local, e não `useFilters`: aquele hook escreve na URL (nuqs) e
  // brigaria com os filtros da própria tela por trás da modal.
  const [search, setSearch] = useState(initialFilters.search ?? '');
  const [advisoryId, setAdvisoryId] = useState(initialFilters.advisoryId ?? '');
  const [city, setCity] = useState(initialFilters.city ?? '');
  const [state, setState] = useState(initialFilters.state ?? '');
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [selectAllError, setSelectAllError] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search, 500);
  const debouncedCity = useDebouncedValue(city, 500);

  const filters = useMemo<BulkCandidateFilters>(
    () => ({
      search: debouncedSearch || undefined,
      advisoryId: advisoryId || undefined,
      city: debouncedCity || undefined,
      state: state || undefined,
    }),
    [debouncedSearch, advisoryId, debouncedCity, state]
  );

  const { items: advisoryOptions } = useAdvisoryFilterOptions(true);
  const {
    brokers,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    fetchAllForFilter,
  } = useBulkBrokerCandidates(filters, true);

  useEffect(() => {
    setSelectAllError(null);
  }, [filters]);

  const loadedSelectedCount = brokers.filter(b => selected.has(b.code)).length;
  const allLoadedSelected =
    brokers.length > 0 && loadedSelectedCount === brokers.length;
  const overLimit = total > BULK_MAX_SELECTION;

  const toggleBroker = (broker: BulkBrokerRef) => {
    const next = new Map(selected);

    if (next.has(broker.code)) {
      next.delete(broker.code);
    } else {
      next.set(broker.code, broker);
    }

    onChange(next);
  };

  const toggleAllFromFilter = async () => {
    if (allLoadedSelected && selected.size > 0) {
      onChange(new Map());
      return;
    }

    setIsSelectingAll(true);
    setSelectAllError(null);

    try {
      const all = await fetchAllForFilter();
      const next = new Map(selected);
      all.forEach(broker => next.set(broker.code, broker));
      onChange(next);
    } catch (err) {
      setSelectAllError(
        err instanceof Error ? err.message : 'Erro ao selecionar as corretoras'
      );
    } finally {
      setIsSelectingAll(false);
    }
  };

  return (
    <section className='flex min-w-0 flex-col gap-3'>
      <header className='flex flex-col gap-1'>
        <h3 className='text-sm font-bold uppercase tracking-[0.04em] text-alba-gray-600'>
          1. Corretoras
        </h3>
      </header>

      <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
        <SelectFilterField
          name='advisoryId'
          type='string'
          value={advisoryId}
          onChange={value => setAdvisoryId(String(value ?? ''))}
          items={advisoryOptions}
          placeholder='Assessoria'
          className='sm:col-span-2'
        />
        <SelectFilterField
          name='state'
          type='string'
          value={state}
          onChange={value => setState(String(value ?? ''))}
          items={BRAZIL_UF_SELECT_OPTIONS}
          placeholder='Estado'
        />
        <SearchFilterField
          value={city}
          onChange={value => setCity(String(value ?? ''))}
          placeholder='Cidade'
        />
        <SearchFilterField
          value={search}
          onChange={value => setSearch(String(value ?? ''))}
          placeholder='Nome, CNPJ ou e-mail'
          className='sm:col-span-2'
        />
      </div>

      <div className='flex items-center justify-between text-sm text-alba-gray-600'>
        <span>
          {isLoading ? 'Carregando…' : `${total} corretora(s) no filtro`}
        </span>
        {selected.size > 0 && (
          <button
            type='button'
            onClick={() => onChange(new Map())}
            disabled={disabled}
            className='font-medium text-alba-gray-600 underline hover:text-alba-black-500'
          >
            Limpar seleção
          </button>
        )}
      </div>

      <div className='flex items-center gap-2 rounded-lg bg-alba-gray-100 px-3 py-2'>
        {/* O HiddenInput e obrigatorio: sem ele o Ark nao dispara onCheckedChange. */}
        <Checkbox.Root
          size='small'
          checked={allLoadedSelected}
          disabled={disabled || isSelectingAll || overLimit || total === 0}
          onCheckedChange={() => void toggleAllFromFilter()}
        >
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.HiddenInput />
        </Checkbox.Root>
        <span className='text-sm text-alba-gray-700'>
          {isSelectingAll
            ? 'Selecionando…'
            : `Selecionar todas as ${total} do filtro`}
        </span>
      </div>

      {overLimit && (
        // Truncar em silêncio seria pior: o admin acharia que aplicou em todas.
        <p className='text-xs text-alba-error-red-500'>
          O filtro retorna {total} corretoras. Refine até {BULK_MAX_SELECTION}{' '}
          ou menos para usar “selecionar todas” — a seleção manual continua
          liberada.
        </p>
      )}

      {selectAllError && (
        <p className='text-xs text-alba-error-red-500'>{selectAllError}</p>
      )}

      {/* Altura fixa e curta: a coluna esquerda nao pode passar do corpo da
          modal, senao aparece um segundo scroll (o da modal alem do da lista). */}
      <div className='h-[240px] overflow-y-auto rounded-xl border border-alba-gray-200'>
        {error ? (
          <p className='p-4 text-sm text-alba-error-red-500'>
            Erro ao carregar corretores.
          </p>
        ) : isLoading ? (
          <p className='p-4 text-sm text-alba-gray-600'>Carregando…</p>
        ) : brokers.length === 0 ? (
          <p className='p-4 text-sm text-alba-gray-600'>
            Nenhuma corretora encontrada com esses filtros.
          </p>
        ) : (
          <ul className='divide-y divide-alba-gray-200'>
            {brokers.map(broker => (
              <li key={broker.code}>
                {/* Linha inteira clicavel, mas NAO como <label>: com o
                    HiddenInput dentro, o label somaria seu proprio toggle ao do
                    checkbox e o clique se anularia. O stopPropagation garante um
                    unico handler por clique. */}
                <div
                  role='button'
                  tabIndex={-1}
                  onClick={() => !disabled && toggleBroker(broker)}
                  className='flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-alba-gray-100'
                >
                  <span onClick={event => event.stopPropagation()}>
                    <Checkbox.Root
                      size='small'
                      checked={selected.has(broker.code)}
                      disabled={disabled}
                      onCheckedChange={() => toggleBroker(broker)}
                      aria-label={`Selecionar ${broker.name}`}
                    >
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Checkbox.HiddenInput />
                    </Checkbox.Root>
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-sm text-alba-black-500'>
                      {broker.name}
                    </span>
                    <span className='block text-xs text-alba-gray-600'>
                      {[broker.document, broker.location]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {hasNextPage && (
          <div className='p-2'>
            <button
              type='button'
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage || disabled}
              className='w-full rounded-lg border border-alba-gray-200 py-2 text-sm text-alba-gray-700 hover:bg-alba-gray-100 disabled:opacity-50'
            >
              {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
            </button>
          </div>
        )}
      </div>

      <p className='text-sm text-alba-gray-700'>
        <strong>{selected.size}</strong> selecionada(s)
        {selected.size > BULK_MAX_SELECTION && (
          <span className='text-alba-error-red-500'>
            {' '}
            — máximo de {BULK_MAX_SELECTION} por operação
          </span>
        )}
      </p>
    </section>
  );
}
